<script setup>
import { ref, watch, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import Modal from 'dashboard/components/Modal.vue';
import pipelineAutomationsAPI from 'dashboard/api/pipelineAutomations';

const props = defineProps({
  name: {
    type: String,
    default: '',
  },
  active: {
    type: Boolean,
    default: true,
  },
  isSaving: {
    type: Boolean,
    default: false,
  },
  automationId: {
    type: [String, Number],
    default: null,
  },
});

const emit = defineEmits(['update:name', 'update:active', 'save', 'cancel']);

const { t } = useI18n();
const { showAlert } = useAlert();

// Export
const isExporting = ref(false);

const handleExport = async () => {
  if (!props.automationId || isExporting.value) return;
  isExporting.value = true;
  try {
    const { data } = await pipelineAutomationsAPI.exportAutomation(
      props.automationId
    );
    const blob = new Blob([JSON.stringify(data, null, 2)], {
      type: 'application/json',
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    const safeName = (props.name || 'automation')
      .replace(/[^a-z0-9_-]/gi, '_')
      .toLowerCase();
    link.href = url;
    link.download = `${safeName}_automation.json`;
    link.click();
    URL.revokeObjectURL(url);
    showAlert(t('PIPELINE.FLOW_BUILDER.EXPORT_SUCCESS'));
  } catch {
    showAlert(t('PIPELINE.FLOW_BUILDER.EXPORT_ERROR'));
  } finally {
    isExporting.value = false;
  }
};
const localName = ref(props.name);
const localActive = ref(props.active);

// Inline title editing
const isEditingTitle = ref(false);
const titleInputRef = ref(null);

const startEditingTitle = () => {
  isEditingTitle.value = true;
  nextTick(() => {
    titleInputRef.value?.focus();
    titleInputRef.value?.select();
  });
};

const commitTitleEdit = () => {
  if (!isEditingTitle.value) return;
  isEditingTitle.value = false;
  const trimmed = localName.value.trim();
  emit('update:name', trimmed);
};

const cancelTitleEdit = () => {
  isEditingTitle.value = false;
};

// Save-time name modal
const showSaveModal = ref(false);
const modalNameInput = ref('');
const modalInputRef = ref(null);

watch(
  () => props.name,
  newValue => {
    localName.value = newValue;
  }
);

watch(
  () => props.active,
  newValue => {
    localActive.value = newValue;
  }
);

const handleActiveChange = () => {
  emit('update:active', localActive.value);
};

const handleSaveClick = () => {
  if (localName.value.trim()) {
    emit('save');
  } else {
    modalNameInput.value = '';
    showSaveModal.value = true;
    nextTick(() => {
      modalInputRef.value?.focus();
    });
  }
};

const handleModalSave = () => {
  const trimmed = modalNameInput.value.trim();
  if (!trimmed) return;
  localName.value = trimmed;
  emit('update:name', trimmed);
  showSaveModal.value = false;
  emit('save');
};

const handleModalCancel = () => {
  showSaveModal.value = false;
};
</script>

<template>
  <div
    class="flex justify-between items-center p-4 border-b border-n-weak h-16 bg-n-solid-2"
  >
    <div class="flex items-center gap-3">
      <button
        class="p-2 rounded-full hover:bg-n-alpha-1 text-n-slate-11 transition-colors"
        @click="$emit('cancel')"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <path d="M19 12H5M12 19l-7-7 7-7" />
        </svg>
      </button>
      <input
        v-if="isEditingTitle"
        ref="titleInputRef"
        v-model="localName"
        type="text"
        class="text-heading-1 text-n-slate-12 px-1 bg-transparent border-b border-n-brand focus:outline-none min-w-[12rem] max-w-xs"
        :placeholder="t('PIPELINE.AUTOMATIONS.FLOW_EDITOR.NAME_PLACEHOLDER')"
        @blur="commitTitleEdit"
        @keydown.enter.prevent="commitTitleEdit"
        @keydown.esc.prevent="cancelTitleEdit"
      />
      <span
        v-else
        class="text-heading-1 text-n-slate-12 px-1 cursor-text rounded hover:bg-n-alpha-1 transition-colors"
        :title="t('PIPELINE.AUTOMATIONS.FLOW_EDITOR.EDIT_TITLE_HINT')"
        @click="startEditingTitle"
      >
        {{
          localName.trim() ||
          t('PIPELINE.AUTOMATIONS.FLOW_EDITOR.NAME_PLACEHOLDER')
        }}
      </span>
    </div>

    <div class="flex items-center gap-3">
      <!-- Export button (only for saved automations) -->
      <button
        v-if="automationId"
        class="flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-n-alpha-1 text-n-slate-11 text-sm font-medium hover:bg-n-alpha-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        :disabled="isExporting"
        @click="handleExport"
      >
        <span class="i-lucide-download size-4" />
        {{ isExporting ? '...' : t('PIPELINE.FLOW_BUILDER.EXPORT') }}
      </button>

      <div class="flex items-center gap-2">
        <label
          class="flex items-center gap-2 text-sm font-medium text-n-slate-12 cursor-pointer"
        >
          {{ t('PIPELINE.AUTOMATIONS.FLOW_EDITOR.ACTIVE') }}
          <span class="relative">
            <input
              v-model="localActive"
              type="checkbox"
              class="sr-only peer"
              @change="handleActiveChange"
            />
            <span
              class="block w-10 h-5 bg-n-alpha-2 rounded-full peer-checked:bg-n-brand transition-colors"
            />
            <span
              class="absolute left-1 top-1 w-3 h-3 bg-white rounded-full transition-transform peer-checked:translate-x-[18px]"
            />
          </span>
        </label>
      </div>

      <button
        class="flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-n-brand text-white text-sm font-medium hover:opacity-90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-n-brand/40"
        :disabled="isSaving"
        @click="handleSaveClick"
      >
        <span
          v-if="isSaving"
          class="i-lucide-loader-2 size-4 animate-spin"
          aria-hidden="true"
        />
        <span v-else class="i-lucide-save size-4" aria-hidden="true" />
        {{
          isSaving
            ? t('PIPELINE.AUTOMATIONS.FLOW_EDITOR.SAVING')
            : t('PIPELINE.AUTOMATIONS.FLOW_EDITOR.SAVE')
        }}
      </button>

      <button
        class="flex items-center gap-1 px-3 py-1.5 rounded-md bg-n-alpha-1 text-n-slate-11 text-sm font-medium hover:bg-n-alpha-2 transition-colors"
        @click="$emit('cancel')"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <line x1="18" y1="6" x2="6" y2="18" />
          <line x1="6" y1="6" x2="18" y2="18" />
        </svg>
        {{ t('PIPELINE.AUTOMATIONS.FLOW_EDITOR.CANCEL') }}
      </button>
    </div>
  </div>

  <!-- Name prompt modal — shown when saving without a name -->
  <Modal v-model:show="showSaveModal" :on-close="handleModalCancel">
    <div class="px-8 py-7 w-full">
      <!-- Icon + heading -->
      <div class="flex items-start gap-4 mb-6">
        <div
          class="flex-shrink-0 w-10 h-10 rounded-xl bg-n-brand/10 flex items-center justify-center"
        >
          <span class="i-lucide-zap size-5 text-n-brand" />
        </div>
        <div>
          <h3 class="text-base font-semibold text-n-slate-12 leading-snug">
            {{ t('PIPELINE.AUTOMATIONS.SAVE_MODAL.TITLE') }}
          </h3>
          <p class="text-sm text-n-slate-10 mt-0.5">
            {{ t('PIPELINE.AUTOMATIONS.SAVE_MODAL.DESCRIPTION') }}
          </p>
        </div>
      </div>

      <!-- Input -->
      <label
        class="block text-xs font-medium text-n-slate-11 mb-1.5 uppercase tracking-wide"
      >
        {{ t('PIPELINE.AUTOMATIONS.SAVE_MODAL.NAME_LABEL') }}
      </label>
      <input
        ref="modalInputRef"
        v-model="modalNameInput"
        type="text"
        class="w-full px-3 py-2.5 text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12 placeholder:text-n-slate-9 focus:outline-none focus:ring-2 focus:ring-n-brand/30 focus:border-n-brand/50 transition-shadow"
        :placeholder="t('PIPELINE.AUTOMATIONS.SAVE_MODAL.NAME_PLACEHOLDER')"
        @keydown.enter="handleModalSave"
      />

      <!-- Actions -->
      <div class="flex gap-2 mt-6">
        <button
          class="flex-1 py-2 text-sm font-medium rounded-lg bg-n-alpha-1 text-n-slate-11 hover:bg-n-alpha-2 transition-colors"
          @click="handleModalCancel"
        >
          {{ t('PIPELINE.AUTOMATIONS.SAVE_MODAL.CANCEL') }}
        </button>
        <button
          class="flex-1 py-2 text-sm font-medium rounded-lg bg-n-brand text-white hover:opacity-90 transition-opacity disabled:opacity-40 disabled:cursor-not-allowed"
          :disabled="!modalNameInput.trim()"
          @click="handleModalSave"
        >
          {{ t('PIPELINE.AUTOMATIONS.SAVE_MODAL.SAVE') }}
        </button>
      </div>
    </div>
  </Modal>
</template>
